import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["type"];
import { convertJsonToFieldValueAvro } from 'rpc-client-utils';
import { noopTelemetry } from '../chatTelemetry';
import { createDeltaAccumulator } from './deltaAccumulator';
import { CONTENT_BLOCK_TYPENAME_PREFIX, ABLY_TYPE_TO_CHIRP_SUFFIX } from './ablyConstants';
import { unwrapTurnMessages, mapBreezeRpcError, isTurnTerminal, isTurnComplete, isAwaitingApproval, extractToolUseBlocks, collectResolvedToolUseIds, getToolUseIdForResult, findLastAssistantMessage } from '../utils/chatHelpers';
function mapAblyContentBlock(block) {
  var _ABLY_TYPE_TO_CHIRP_S;
  const ablyType = block.type;
  const suffix = (_ABLY_TYPE_TO_CHIRP_S = ABLY_TYPE_TO_CHIRP_SUFFIX[ablyType]) !== null && _ABLY_TYPE_TO_CHIRP_S !== void 0 ? _ABLY_TYPE_TO_CHIRP_S : `${ablyType}ChirpContentBlock`;
  const rest = _objectWithoutPropertiesLoose(block, _excluded);
  if ((ablyType === 'TOOL_USE' || ablyType === 'SERVER_TOOL_USE') && rest.input != null && typeof rest.input === 'object' && !('__typename' in rest.input)) {
    rest.input = convertJsonToFieldValueAvro(rest.input);
  }
  return Object.assign({}, rest, {
    __typename: `${CONTENT_BLOCK_TYPENAME_PREFIX}${suffix}`
  });
}
function isAblyFullMessage(event) {
  return 'id' in event && 'role' in event && 'content' in event && !('type' in event);
}
function mapAblyFullMessage(event) {
  const content = event.content.map(mapAblyContentBlock);
  return Object.assign({}, event, {
    content
  });
}
function createDeferred() {
  let resolve;
  const promise = new Promise(resolveFn => {
    resolve = resolveFn;
  });
  return {
    promise,
    resolve: resolve
  };
}
// A turn that goes quiet is verified against the store rather than failed
// outright (see handleStreamSilence), so this cap is the only thing that ends a
// genuinely hung turn: 20 checks x 30s bounds the spinner at ~10 minutes before
// the user is told something went wrong.
const DEFAULT_MAX_STALL_LIVENESS_CHECKS = 20;
export function createAblyTransport(connection, logger, options = {}, telemetry = noopTelemetry) {
  var _options$isClientTool;
  const {
    cancelInterruptTimeoutMs = 10000,
    maxStallLivenessChecks = DEFAULT_MAX_STALL_LIVENESS_CHECKS
  } = options;
  // Mutable so createChatClient can install a predicate that tracks runtime
  // setTools() changes (setClientTool below), keeping the watchdog in sync with
  // the loader. Defaults to "nothing is client-side" (pre-existing behavior).
  let isClientTool = (_options$isClientTool = options.isClientTool) !== null && _options$isClientTool !== void 0 ? _options$isClientTool : () => false;
  let isProcessingTurn = false;
  let turnDeferred = null;
  let postStreamQueue = [];
  const deltaAccumulator = createDeltaAccumulator();
  let fullMessageReceived = false;
  // true when a full message has already handled (or intentionally skipped) its
  // stop reason, so MESSAGE_END should not fire resolveTurn a second time
  let fullMessageHandledStopReason = false;
  // Guards against Ably channel rewind replaying historical events from a
  // previous turn and prematurely ending the new turn via resolveTurn. Set to
  // true either by onChannelReady (when preconnect already consumed rewind) or
  // by onMetadata (after sendMessageRpc returns).
  let acceptingTurnEvents = false;
  let cancelFallbackTimer = null;
  // Silence threshold: after this much wall-clock time with no stream activity,
  // verify the turn against the store.
  const STREAM_STALL_TIMEOUT_MS = 30000;
  // How often the watchdog re-evaluates elapsed silence. Well below the
  // threshold so the crossing is detected within ~1s. The watchdog compares
  // measured wall-clock time (see lastActivityAt), not the number of ticks, so
  // a tab whose timers were throttled/frozen in the background still recovers:
  // the first tick after the tab is unfrozen sees the real elapsed gap. This is
  // why we poll instead of arming a single setTimeout(30s) — a frozen one-shot
  // timer's fire time says nothing about how long silence actually lasted.
  const STREAM_STALL_CHECK_INTERVAL_MS = 1000;
  let streamStallTimer = null;
  // Wall-clock time of the last stream activity (or turn (re)attach). null when
  // no watchdog is armed. The watchdog fires a store check once
  // now - lastActivityAt crosses STREAM_STALL_TIMEOUT_MS.
  let lastActivityAt = null;
  // Ids of the turn currently streaming, needed to ask the store whether a
  // silent turn is still running. Set from the send/resume metadata callbacks.
  let activeConversationId = null;
  let activeTurnId = null;
  // Consecutive silence windows in which the store still reported the turn
  // running with no new persisted message. Reset by any inbound event and by
  // the store reporting progress, so it counts uninterrupted lack of progress.
  let consecutiveSilentChecks = 0;
  // The last message id the store reported for this turn, used to tell whether
  // the turn made persisted progress between two silence checks (progress
  // resets the budget). Seeded from the resume request's lastMessageId.
  let lastProgressMessageId = null;
  // Opaque token for "which turn is streaming right now", bumped whenever a
  // turn starts or is torn down. A liveness check captures it and discards its
  // own result if the token moved on, so a slow check can never apply one
  // turn's outcome to another. Handler identity is not enough: `Transport` is a
  // public interface and an external consumer may pass a stable handler object.
  let turnGeneration = 0;
  // Generation of the check currently awaiting the store, or null if none.
  let livenessCheckGeneration = null;
  // Count of outstanding stale CANCELLED messages to drop. Incremented each
  // time the fallback timer fires (BE may still publish late). Decremented as
  // each stale message is received and dropped. A counter rather than a boolean
  // is necessary to handle consecutive fallback-cancelled turns correctly: if
  // the user cancels turn B while A's stale is still in-flight, the stale from
  // A must still be dropped even though cancelTurn() was called for B.
  let staleInterruptCount = 0;
  // Current turn's handler; kept alive across AWAITING pauses so a cancel then
  // can still drive the turn to completion. Cleared only on full teardown.
  let activeSendHandler = null;
  let turnMetadataDeferred = null;
  function resolveTurnMetadata() {
    if (turnMetadataDeferred) {
      turnMetadataDeferred.resolve();
      turnMetadataDeferred = null;
    }
  }
  function clearCancelFallback() {
    if (cancelFallbackTimer !== null) {
      clearTimeout(cancelFallbackTimer);
      cancelFallbackTimer = null;
    }
  }
  function clearStreamStallTimer() {
    if (streamStallTimer !== null) {
      clearInterval(streamStallTimer);
      streamStallTimer = null;
    }
    lastActivityAt = null;
  }

  // Records the current time as the start of a fresh silence window and ensures
  // the watchdog poll is running. Cheap to call on every event: it only stamps
  // lastActivityAt and starts the interval once.
  function startStreamStallTimer() {
    lastActivityAt = Date.now();
    if (streamStallTimer !== null) return;
    streamStallTimer = setInterval(() => {
      // Self-terminate once the turn is no longer streaming. Transport-error
      // teardowns (send/tool onError, a top-level ERROR frame) set
      // isProcessingTurn = false without routing through clearStreamStallTimer,
      // so without this the repeating interval would outlive the turn and keep
      // ticking for the life of the transport. This bounds any such orphan to a
      // single tick and keeps the watchdog owning its own lifecycle.
      if (!isProcessingTurn) {
        clearStreamStallTimer();
        return;
      }
      if (lastActivityAt !== null && Date.now() - lastActivityAt >= STREAM_STALL_TIMEOUT_MS) {
        // Threshold crossed: stop polling and hand off to the store check.
        // handleStreamSilence re-arms via startStreamStallTimer while the turn
        // is still running, and tears down (resolveAndCleanup) otherwise — so
        // the interval shouldn't keep firing (and re-invoking handleStreamSilence
        // only to hit its in-flight-check guard) while getTurnResult is
        // outstanding. Also resets lastActivityAt so a re-armed window starts clean.
        clearStreamStallTimer();
        void handleStreamSilence();
      }
    }, STREAM_STALL_CHECK_INTERVAL_MS);
  }

  // Any inbound event means the stream is alive: restart the silence window and
  // forget the silence we had accumulated.
  function onStreamActivity() {
    consecutiveSilentChecks = 0;
    startStreamStallTimer();
  }
  function failStalledTurn(handler, error) {
    telemetry.streamStalled();
    handler.onError(error);
    isProcessingTurn = false;
    resolveAndCleanup();
  }

  // 30 seconds of Ably silence does not mean the turn is dead. One long tool
  // call, or first-token latency on a large context, routinely exceeds it. So
  // ask the store what the turn is actually doing before telling the user
  // anything: failing a live turn here abandons work the backend goes on to
  // finish, and asks the user to resend a request that was never lost.
  async function handleStreamSilence() {
    if (!isProcessingTurn || !activeSendHandler) return;
    const handler = activeSendHandler;
    const generation = turnGeneration;
    const conversationId = activeConversationId;
    const turnId = activeTurnId;
    // Only one check at a time per turn. A check left over from an earlier turn
    // is deliberately NOT a reason to bail: it will discard itself when it
    // lands, and returning here would leave this turn with no armed timer and
    // so no way to ever recover.
    if (livenessCheckGeneration === generation) return;

    // getTurnResult is optional on the public AblyBreezeConnection interface, so
    // a custom connection supplied via `__ablyConnection` may not implement it.
    const getTurnResult = connection.getTurnResult;
    if (!getTurnResult || conversationId == null || turnId == null) {
      telemetry.streamSilence('unverifiable');
      failStalledTurn(handler, {
        type: 'TRANSPORT_ERROR',
        message: 'Stream stalled: no events for 30s and the turn could not be verified against the store'
      });
      return;
    }
    if (consecutiveSilentChecks >= maxStallLivenessChecks) {
      telemetry.streamSilence('exhausted');
      failStalledTurn(handler, {
        type: 'TRANSPORT_ERROR',
        message: `Stream stalled: turn still reported as running after ${maxStallLivenessChecks} silent checks`
      });
      return;
    }
    consecutiveSilentChecks++;
    livenessCheckGeneration = generation;
    let result;
    try {
      // Invoke with `connection` as the receiver: a custom __ablyConnection may
      // implement getTurnResult as a method that relies on `this`.
      result = await getTurnResult.call(connection, {
        conversationId,
        turnId
      });
    } catch (error) {
      if (isProcessingTurn && generation === turnGeneration) {
        telemetry.streamSilence('unverifiable');
        failStalledTurn(handler, mapBreezeRpcError(error));
      }
      return;
    } finally {
      // Only clear the slot if a later turn has not already claimed it.
      if (livenessCheckGeneration === generation) {
        livenessCheckGeneration = null;
      }
    }

    // The turn this check was asking about is over -- events resolved it, or it
    // was torn down and another turn started. Either way that path owns the
    // teardown and this answer is stale, so drop it.
    if (!isProcessingTurn || generation !== turnGeneration) return;
    if (!isTurnTerminal(result.status)) {
      var _pauseExtracted$toolU, _findLastAssistantMes, _pauseExtracted$toolU2;
      // A turn parked on a client-side tool (ask_user_questions, an approval
      // tool) is waiting on the user, not working, so the silence budget would
      // spin to exhaustion and strand the UI. The store is authoritative:
      // detect the pause the way handleExternalTool does (an unresolved
      // tool_use block for a tool THIS client executes) since a client-tool
      // pause persists as TOOL_USE, not EXTERNAL_TOOL, then route through
      // onStopReason so the resolver surfaces the pending tool/approval.
      const storeMessages = unwrapTurnMessages(result.messages);
      const pauseExtracted = extractToolUseBlocks(storeMessages);
      const resolvedToolUseIds = collectResolvedToolUseIds(storeMessages);
      const isUnresolved = block => !resolvedToolUseIds.has(getToolUseIdForResult(block));
      const hasPendingClientTool = (_pauseExtracted$toolU = pauseExtracted === null || pauseExtracted === void 0 ? void 0 : pauseExtracted.toolUseBlocks.some(block => isClientTool(block.name) && isUnresolved(block))) !== null && _pauseExtracted$toolU !== void 0 ? _pauseExtracted$toolU : false;
      // Only treat an approval pause as active while the approval is still
      // unresolved — otherwise a snapshot whose approval already has a result
      // (but whose last assistant still reads TOOLS_REQUIRE_APPROVAL) would
      // re-surface a stale prompt. Mirrors the loader's gating.
      const isApprovalPause = isAwaitingApproval((_findLastAssistantMes = findLastAssistantMessage(storeMessages)) === null || _findLastAssistantMes === void 0 ? void 0 : _findLastAssistantMes.stopReason) && ((_pauseExtracted$toolU2 = pauseExtracted === null || pauseExtracted === void 0 ? void 0 : pauseExtracted.toolUseBlocks.some(isUnresolved)) !== null && _pauseExtracted$toolU2 !== void 0 ? _pauseExtracted$toolU2 : false);
      if (hasPendingClientTool || isApprovalPause) {
        telemetry.streamSilence('recovered');
        telemetry.clientPauseRestored({
          source: 'live-watchdog',
          kind: isApprovalPause ? 'approval' : 'external-tool'
        });
        logger.transportRecovery('success', {
          stopReason: isApprovalPause ? 'TOOLS_REQUIRE_APPROVAL' : 'TOOL_USE',
          conversationId,
          turnId
        });
        if (storeMessages.length > 0) {
          handler.onMessageUpdate({
            type: 'reconcile-all',
            messages: storeMessages
          });
        }
        isProcessingTurn = false;
        firePostStreamActions(handler);
        // The resolver keys client-side handling off EXTERNAL_TOOL /
        // TOOLS_REQUIRE_APPROVAL (handleStopReason), so pass the mapped signal
        // — the persisted reason is TOOL_USE, which it would treat as complete.
        // try/finally so a throwing onStopReason still tears the turn down.
        try {
          await handler.onStopReason(isApprovalPause ? 'TOOLS_REQUIRE_APPROVAL' : 'EXTERNAL_TOOL');
        } finally {
          resolveAndCleanup();
        }
        return;
      }
      telemetry.streamSilence('turn-running');
      // Progress-aware budget: if the store persisted a new message since the
      // last check, the turn is advancing (just not over Ably), so reset the
      // silence budget. This makes the cap "N checks with no persisted
      // progress" rather than "N checks of wall-clock silence".
      if (result.lastMessageId != null && result.lastMessageId !== lastProgressMessageId) {
        lastProgressMessageId = result.lastMessageId;
        consecutiveSilentChecks = 0;
      }
      startStreamStallTimer();
      return;
    }
    if (result.status === 'FAILED') {
      telemetry.streamSilence('turn-failed');
      failStalledTurn(handler, {
        type: 'UNKNOWN',
        message: 'Turn failed'
      });
      return;
    }

    // Terminal and not failed: the turn finished but its closing events were
    // lost in transit. The store is authoritative, so reconcile from it and end
    // the turn normally -- an error banner here would be reporting a failure for
    // work that actually succeeded.
    telemetry.streamSilence('recovered');
    logger.transportRecovery('success', {
      stopReason: String(result.status),
      conversationId,
      turnId
    });
    const messages = unwrapTurnMessages(result.messages);
    if (messages.length > 0) {
      handler.onMessageUpdate({
        type: 'reconcile-all',
        messages
      });
    }
    isProcessingTurn = false;
    firePostStreamActions(handler);
    await handler.onComplete();
    resolveAndCleanup();
  }
  async function resolveTurn(stopReason, handler) {
    var _turnMetadataDeferred;
    const metadataReady = (_turnMetadataDeferred = turnMetadataDeferred) === null || _turnMetadataDeferred === void 0 ? void 0 : _turnMetadataDeferred.promise;
    if (metadataReady) {
      await metadataReady;
    }
    clearCancelFallback();
    clearStreamStallTimer();
    const mappedStopReason = stopReason === 'TOOL_USE' ? 'EXTERNAL_TOOL' : stopReason;
    isProcessingTurn = false;
    const currentDeferred = turnDeferred;
    turnDeferred = null;
    firePostStreamActions(handler);
    await handler.onStopReason(mappedStopReason);
    currentDeferred === null || currentDeferred === void 0 || currentDeferred.resolve();
  }

  // Recovers an event whose payload exceeded Ably's message-size limit. The
  // backend publishes a small `{ exceedsSize: true, conversationId, turnId }`
  // envelope in place of the real payload; we refetch the turn's authoritative
  // messages from the store and reconcile them (replace-all), the same emit the
  // polling transport uses. Nothing is lost — the full content is always
  // persisted regardless of what streamed over Ably.
  async function refetchOversizedTurn(event, handler) {
    const conversationId = event.conversationId;
    const turnId = event.turnId;
    if (conversationId == null || turnId == null) {
      handler.onError({
        type: 'TRANSPORT_ERROR',
        message: 'Received an oversized Ably event without conversationId/turnId; cannot refetch the turn.'
      });
      return;
    }
    // getTurnResult is optional on the public AblyBreezeConnection interface, so
    // a custom connection supplied via `__ablyConnection` may not implement it.
    const getTurnResult = connection.getTurnResult;
    if (!getTurnResult) {
      handler.onError({
        type: 'TRANSPORT_ERROR',
        message: 'Received an oversized Ably event but the connection cannot refetch turns; the event was dropped.'
      });
      return;
    }
    try {
      // Invoke with `connection` as the receiver: a custom __ablyConnection may
      // implement getTurnResult as a method that relies on `this`.
      const result = await getTurnResult.call(connection, {
        conversationId,
        turnId
      });
      const messages = unwrapTurnMessages(result.messages);
      if (messages.length > 0) {
        handler.onMessageUpdate({
          type: 'reconcile-all',
          messages
        });
      }
    } catch (error) {
      handler.onError(mapBreezeRpcError(error));
    }
  }
  function processAblyEvent(event, handler) {
    var _handler$onStreamEven;
    // An oversized event was replaced by the backend with a small envelope
    // carrying only { exceedsSize, conversationId, turnId }. Refetch the full
    // turn rather than trying to render the omitted payload.
    if (event.exceedsSize === true) {
      void refetchOversizedTurn(event, handler);
      return;
    }
    const isFullMsg = isAblyFullMessage(event);

    // Drop stale CANCELLED messages before any consumer callback fires.
    // The backend defers its Ably publish until after all DB writes, so a
    // CANCELLED message from a cancelled turn can arrive during the next turn's
    // subscription window (after the 5s fallback resolved the previous turn).
    if (isFullMsg) {
      const stopReason = event.stopReason;
      if (stopReason === 'CANCELLED' && staleInterruptCount > 0) {
        staleInterruptCount--;
        return;
      }
    }
    logger.transportStreamEvent(event);
    logger.ablyRawEvent(event);
    (_handler$onStreamEven = handler.onStreamEvent) === null || _handler$onStreamEven === void 0 || _handler$onStreamEven.call(handler, event);
    if (acceptingTurnEvents) {
      onStreamActivity();
    }
    const eventType = event.type;
    if (eventType === 'MESSAGE_START') {
      fullMessageReceived = false;
      fullMessageHandledStopReason = false;
    }
    if (isFullMsg) {
      fullMessageReceived = true;
      deltaAccumulator.reset();
      const role = event.role;
      const stopReason = event.stopReason;
      const subagentSource = event.subagentSource;
      if (role === 'ERROR') {
        var _map$join, _content;
        if (!acceptingTurnEvents) return;
        const rawText = (_map$join = (_content = event.content) === null || _content === void 0 ? void 0 : _content.map(block => block.text).join('')) !== null && _map$join !== void 0 ? _map$join : 'Unknown error';
        // A subagent-scoped error is not fatal to the parent turn — the backend
        // retries/continues and the turn goes on to finish. Terminating here
        // strands the UI with "Something went wrong" on a turn that actually
        // recovers; the failure only surfaces when a subagent step transiently
        // errors, which is why it was intermittent. Mirror the subagent guard
        // used for stop reasons below (subagentSource?.toolCallId) and record
        // it for observability instead of killing the whole turn.
        if (subagentSource !== null && subagentSource !== void 0 && subagentSource.toolCallId) {
          telemetry.subagentError(rawText);
          return;
        }
        isProcessingTurn = false;
        resolveTurnMetadata();
        const currentDeferred = turnDeferred;
        turnDeferred = null;
        firePostStreamActions(handler);
        handler.onError({
          type: 'UNKNOWN',
          message: 'Something went wrong. Please try again.',
          cause: new Error(rawText)
        });
        currentDeferred === null || currentDeferred === void 0 || currentDeferred.resolve();
        return;
      }
      const message = mapAblyFullMessage(event);
      handler.onMessageUpdate({
        type: 'append',
        message,
        stopReason: subagentSource !== null && subagentSource !== void 0 && subagentSource.toolCallId ? undefined : stopReason !== null && stopReason !== void 0 ? stopReason : undefined
      });
      if (stopReason && acceptingTurnEvents) {
        // Set before the subagent guard: the MESSAGE_END handler checks this
        // flag to avoid double-resolving. Without it, a subagent END_TURN
        // would skip resolveTurn here but the MESSAGE_END for the same message
        // would still fire it. Safe across messages because MESSAGE_START
        // resets the flag for each new message.
        fullMessageHandledStopReason = true;
        if (subagentSource !== null && subagentSource !== void 0 && subagentSource.toolCallId) {
          return;
        }
        if (stopReason === 'TOOL_USE') {
          var _contentBlocks$some;
          const contentBlocks = event.content;
          const hasClientToolUseBlock = (_contentBlocks$some = contentBlocks === null || contentBlocks === void 0 ? void 0 : contentBlocks.some(block => block.type === 'TOOL_USE')) !== null && _contentBlocks$some !== void 0 ? _contentBlocks$some : false;
          if (!hasClientToolUseBlock) {
            // All tool calls are server-side — channel continues automatically.
            return;
          }
        }
        void resolveTurn(stopReason, handler);
      }
      return;
    }
    if (eventType === 'MESSAGE_END') {
      const stopReason = event.stopReason;
      if (stopReason && acceptingTurnEvents) {
        if (fullMessageHandledStopReason || stopReason === 'TOOL_USE') {
          // Full message already handled (or skipped) this stop reason, or TOOL_USE
          // stop reason — wait for EXTERNAL_TOOL which the BE defers until after the
          // turn status write, ensuring sendToolResponse never races the DB update.
          return;
        }
        void resolveTurn(stopReason, handler);
      }
      return;
    }
    if (fullMessageReceived) return;
    const deltaMessage = deltaAccumulator.handleEvent(event);
    if (deltaMessage) {
      handler.onMessageUpdate({
        type: 'delta-update',
        message: deltaMessage
      });
    }
  }
  function firePostStreamActions(handler) {
    const actions = postStreamQueue.splice(0);
    for (const action of actions) {
      void action().catch(error => {
        handler.onError(error);
      });
    }
  }
  function resolveAndCleanup() {
    turnGeneration++;
    clearCancelFallback();
    clearStreamStallTimer();
    resolveTurnMetadata();
    activeSendHandler = null;
    connection.unsubscribe();
    postStreamQueue = [];
    if (turnDeferred) {
      turnDeferred.resolve();
      turnDeferred = null;
    }
  }
  const transport = {
    activeStatus: 'STREAMING',
    isPersistentChannel: true,
    setClientTool(predicate) {
      isClientTool = predicate;
    },
    async send(request, handler) {
      var _request$externalSkil, _request$externalArti;
      activeSendHandler = handler;
      isProcessingTurn = true;
      turnDeferred = createDeferred();
      acceptingTurnEvents = false;
      // Cleared so a silence check can never verify the previous turn's ids.
      activeConversationId = null;
      activeTurnId = null;
      consecutiveSilentChecks = 0;
      lastProgressMessageId = null;
      turnGeneration++;
      const chirpRequest = Object.assign({
        agentId: request.agentId
      }, request.conversationId ? {
        conversationId: request.conversationId
      } : {}, request.collectionId ? {
        collectionId: request.collectionId
      } : {}, {
        message: request.message
      }, request.turnMetadata ? {
        turnMetadata: request.turnMetadata
      } : {}, request.conversationMetadata ? {
        conversationMetadata: request.conversationMetadata
      } : {}, request.messageContexts ? {
        messageContexts: request.messageContexts
      } : {}, request.fileReferences ? {
        fileReferences: request.fileReferences
      } : {}, {
        streaming: true,
        ablyEnabled: true
      }, request.configVersion != null ? {
        configVersion: request.configVersion
      } : {}, {
        externalToolSpecs: request.externalToolSpecs,
        externalSkillSpecs: (_request$externalSkil = request.externalSkillSpecs) !== null && _request$externalSkil !== void 0 ? _request$externalSkil : []
      }, request.externalSubagentSpecs ? {
        externalSubagentSpecs: request.externalSubagentSpecs
      } : {}, {
        externalArtifactPluginSpecs: (_request$externalArti = request.externalArtifactPluginSpecs) !== null && _request$externalArti !== void 0 ? _request$externalArti : []
      });
      let firstEventLogged = false;
      const ablyHandler = {
        onEvent(event) {
          if (!firstEventLogged) {
            firstEventLogged = true;
            logger.ablySubscribed('first-event-received');
          }
          processAblyEvent(event, handler);
        },
        onError(error) {
          var _turnDeferred;
          logger.transportError(error);
          telemetry.streamError(error);
          resolveTurnMetadata();
          handler.onError(error);
          isProcessingTurn = false;
          (_turnDeferred = turnDeferred) === null || _turnDeferred === void 0 || _turnDeferred.resolve();
          turnDeferred = null;
        }
      };
      const pendingTurn = turnDeferred.promise;
      let sendResponse;
      try {
        sendResponse = await connection.send(chirpRequest, ablyHandler, (conversationId, turnId, title) => {
          acceptingTurnEvents = true;
          activeConversationId = conversationId;
          activeTurnId = turnId;
          onStreamActivity();
          handler.onTurnStarted(conversationId, turnId, title);
          resolveTurnMetadata();
        }, () => {
          acceptingTurnEvents = true;
          onStreamActivity();
          turnMetadataDeferred = createDeferred();
        });
      } catch (err) {
        ablyHandler.onError(err != null && typeof err === 'object' && 'errorType' in err ? mapBreezeRpcError(err) : {
          type: 'TRANSPORT_ERROR',
          message: err instanceof Error ? err.message : 'Transport send failed',
          cause: err
        });
        return;
      }
      logger.transportSendResponse(request, sendResponse);
      await pendingTurn;
    },
    async sendToolResponse(request, handler) {
      logger.transportToolResponse(request);
      if (isProcessingTurn) {
        postStreamQueue.push(() => doSendToolResponse(request, handler));
        return;
      }
      await doSendToolResponse(request, handler);
    },
    async sendToolApproval(request, handler) {
      if (isProcessingTurn) {
        postStreamQueue.push(() => doSendToolApproval(request, handler));
        return;
      }
      await doSendToolApproval(request, handler);
    },
    async resume(request, handler) {
      var _request$lastMessageI, _response$conversatio, _response$conversatio2;
      logger.transportResume(request);
      activeSendHandler = handler;
      isProcessingTurn = true;
      turnDeferred = createDeferred();
      acceptingTurnEvents = true;
      activeConversationId = request.conversationId;
      activeTurnId = request.turnId;
      consecutiveSilentChecks = 0;
      // Seed progress tracking from the last message the client already has, so
      // the first store check counts "no newer persisted message" as no
      // progress rather than getting a free budget reset.
      lastProgressMessageId = (_request$lastMessageI = request.lastMessageId) !== null && _request$lastMessageI !== void 0 ? _request$lastMessageI : null;
      turnGeneration++;
      const ablyHandler = {
        onEvent(event) {
          processAblyEvent(event, handler);
        },
        onError(error) {
          var _turnDeferred2;
          clearStreamStallTimer();
          logger.transportError(error);
          // Resume-error counting is owned by the resume initiator (e.g.
          // conversationLoader), which distinguishes a real failure from an
          // expected "turn already gone" outcome. Don't count here.
          handler.onError(error);
          isProcessingTurn = false;
          (_turnDeferred2 = turnDeferred) === null || _turnDeferred2 === void 0 || _turnDeferred2.resolve();
          turnDeferred = null;
        }
      };
      const pendingTurn = turnDeferred.promise;
      const response = await connection.getOrCreateAblyChannel(request.conversationId, ablyHandler);
      logger.transportResumeResponse(request, {
        conversationId: (_response$conversatio = response.conversationId) !== null && _response$conversatio !== void 0 ? _response$conversatio : request.conversationId,
        ablyChannelId: response.ablyChannelId
      });
      handler.onTurnStarted((_response$conversatio2 = response.conversationId) !== null && _response$conversatio2 !== void 0 ? _response$conversatio2 : request.conversationId, request.turnId, response.title);

      // Response messages are AviatorChirpMessage (CHIRP-typed with __typename),
      // NOT raw Ably events. Emit directly, don't feed through processAblyEvent.
      const responseMessages = response.messages;
      const lastMessage = responseMessages === null || responseMessages === void 0 ? void 0 : responseMessages[responseMessages.length - 1];
      const lastStopReason = lastMessage === null || lastMessage === void 0 ? void 0 : lastMessage.stopReason;

      // Only a genuinely terminal stop reason ends the turn here. A truthy but
      // non-terminal reason (TOOL_USE, EXTERNAL_TOOL, AWAITING_TOOL_RESULT,
      // TOOLS_REQUIRE_APPROVAL) means the turn is still going, so fall through
      // to the watchdog and wait for the rest of the stream. A turn paused on a
      // client-side tool is kept off the resume path entirely by
      // conversationLoader, which detects that pause via the tool registry and
      // restores it directly — the transport has no registry to make that call.
      if (lastMessage && isTurnComplete(lastStopReason)) {
        clearStreamStallTimer();
        connection.unsubscribe();
        handler.onMessageUpdate({
          type: 'append',
          message: lastMessage
        });
        isProcessingTurn = false;
        const currentDeferred = turnDeferred;
        turnDeferred = null;
        void (async () => {
          await handler.onStopReason(lastStopReason);
          currentDeferred === null || currentDeferred === void 0 || currentDeferred.resolve();
        })();
      } else {
        // The turn is still in progress. Arm the shared store-verified liveness
        // watchdog rather than a bespoke hard timeout: 30s of Ably silence then
        // asks the store whether the turn is running (long tool calls and slow
        // first tokens routinely exceed 30s), and any inbound event re-arms it
        // via onStreamActivity. This is the same machinery send() uses — resume
        // must not diverge. It also covers the case where events delivered
        // synchronously during getOrCreateAblyChannel arrived before the handler
        // pointer was live and were dropped by the connection's !activeHandler
        // guard: the first silence check reconciles from the store.
        startStreamStallTimer();
      }
      await pendingTurn;
    },
    async cancelTurn(request) {
      logger.transportCancel(request);
      // Stay subscribed so the interrupt (stopReason: CANCELLED) can arrive and
      // resolve the turn. If it never does, drive completion via the fallback.
      const handlerForFallback = activeSendHandler;
      const deferredAtCancel = turnDeferred;
      clearCancelFallback();
      cancelFallbackTimer = setTimeout(() => {
        cancelFallbackTimer = null;
        // Skip if another path already terminated this turn (interrupt, ERROR
        // message, or connection error) — they null/replace turnDeferred.
        if (turnDeferred !== deferredAtCancel) {
          return;
        }
        // Fallback fired: the backend may still publish the CANCELLED message
        // late into future subscriptions. staleInterruptCount is a counter (not
        // a boolean) so consecutive fallback-cancelled turns each contribute one
        // expected stale message that gets dropped in order.
        staleInterruptCount++;
        resolveAndCleanup();
        void (handlerForFallback === null || handlerForFallback === void 0 ? void 0 : handlerForFallback.onStopReason('CANCELLED'));
      }, cancelInterruptTimeoutMs);
      try {
        await connection.cancelTurn({
          conversationId: request.conversationId,
          turnId: request.turnId,
          reason: request.reason
        });
      } catch (_unused) {
        // best-effort — fallback timer handles cleanup if no interrupt arrives
      }
    },
    async preconnect(conversationId) {
      if (isProcessingTurn) {
        // Don't tear down the active turn's subscription. send() will
        // re-subscribe to the correct channel after the RPC returns if needed.
        return;
      }
      // preconnect is a best-effort idle optimization; a failure is non-fatal
      // (the next send re-subscribes and surfaces any real error) and never
      // reaches the user, so it must not be counted as a stream-error.
      await connection.preconnect(conversationId).catch(error => {
        logger.transportError(error);
      });
    },
    stop() {
      resolveAndCleanup();
    },
    clear() {
      resolveAndCleanup();
      isProcessingTurn = false;
      acceptingTurnEvents = false;
      staleInterruptCount = 0;
    },
    async submitFeedback(request) {
      try {
        await connection.submitFeedback(request);
      } catch (_unused2) {
        // fire-and-forget
      }
    },
    async steerTurn(request) {
      try {
        return await connection.steerTurn({
          conversationId: request.conversationId,
          turnId: request.turnId,
          message: request.message
        });
      } catch (error) {
        logger.transportError(error);
        return undefined;
      }
    }
  };
  async function doSendToolResponse(request, handler) {
    activeSendHandler = handler;
    isProcessingTurn = true;
    turnDeferred = createDeferred();
    acceptingTurnEvents = true;
    const ablyHandler = {
      onEvent(event) {
        processAblyEvent(event, handler);
      },
      onError(error) {
        var _turnDeferred3;
        logger.transportError(error);
        telemetry.streamError(error);
        handler.onError(error);
        isProcessingTurn = false;
        (_turnDeferred3 = turnDeferred) === null || _turnDeferred3 === void 0 || _turnDeferred3.resolve();
        turnDeferred = null;
      }
    };
    const pendingTurn = turnDeferred.promise;
    try {
      var _request$externalSkil2, _request$externalArti2;
      await connection.sendToolResponse(Object.assign({
        conversationId: request.conversationId,
        turnId: request.turnId,
        toolResults: request.toolResults,
        externalToolSpecs: request.externalToolSpecs,
        externalSkillSpecs: (_request$externalSkil2 = request.externalSkillSpecs) !== null && _request$externalSkil2 !== void 0 ? _request$externalSkil2 : []
      }, request.externalSubagentSpecs ? {
        externalSubagentSpecs: request.externalSubagentSpecs
      } : {}, {
        externalArtifactPluginSpecs: (_request$externalArti2 = request.externalArtifactPluginSpecs) !== null && _request$externalArti2 !== void 0 ? _request$externalArti2 : []
      }, request.turnMetadata && {
        turnMetadata: request.turnMetadata
      }, request.messageContexts && {
        messageContexts: request.messageContexts
      }), ablyHandler);
      logger.transportToolResponseResponse(request, {});
    } catch (err) {
      isProcessingTurn = false;
      const currentDeferred = turnDeferred;
      turnDeferred = null;
      currentDeferred === null || currentDeferred === void 0 || currentDeferred.resolve();
      throw err;
    }
    await pendingTurn;
  }
  async function doSendToolApproval(request, handler) {
    activeSendHandler = handler;
    isProcessingTurn = true;
    turnDeferred = createDeferred();
    acceptingTurnEvents = true;
    const ablyHandler = {
      onEvent(event) {
        processAblyEvent(event, handler);
      },
      onError(error) {
        var _turnDeferred4;
        logger.transportError(error);
        telemetry.streamError(error);
        handler.onError(error);
        isProcessingTurn = false;
        (_turnDeferred4 = turnDeferred) === null || _turnDeferred4 === void 0 || _turnDeferred4.resolve();
        turnDeferred = null;
      }
    };
    const pendingTurn = turnDeferred.promise;
    await connection.sendToolApproval(Object.assign({
      conversationId: request.conversationId,
      turnId: request.turnId,
      approvals: request.approvals
    }, request.turnMetadata && {
      turnMetadata: request.turnMetadata
    }, request.messageContexts && {
      messageContexts: request.messageContexts
    }), ablyHandler);
    await pendingTurn;
  }
  return transport;
}